import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import { CUSTOM_CSS_MAX, checkCustomCss } from '@clawharbor/manifest';
import type { TenantDetail } from '../api.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { ErrorNote } from './bits.js';

/**
 * The site's own widget stylesheet: the CSS half of "make it look like theirs".
 *
 * The brand tokens a crawl extracts give a widget the business's colours and
 * type. This is the rest of the look (how their buttons, cards and form fields
 * are shaped) for a person who knows the business's site better than the
 * crawler does. It is served inside every widget of this site, in claude.ai
 * and ChatGPT alike, after the tokens and the base styles, so it wins.
 *
 * LINTED HERE BY THE SAME RULE THE SERVER APPLIES. `checkCustomCss` lives in
 * @clawharbor/manifest so this panel, the PATCH route and the widget cannot
 * disagree. A stylesheet that reaches out (an import, a url(), a script) is
 * refused before Save is even enabled, with the construct named, because the
 * alternative is a field that saves and then paints nothing.
 *
 * THE PREVIEW SHOWS WHAT IS SAVED. The frame is the real preview route, which
 * renders the live manifest through a real data-plane call, so it changes on
 * Save and not per keystroke. Saying so beside it is cheaper than a second,
 * client-side renderer that would drift from the one chat clients run.
 */

/** The selectors a stylesheet may rely on. Anything else is internal and may move. */
const CONTRACT: ReadonlyArray<readonly [string, string]> = [
  ['.ch-widget', 'the whole panel'],
  ['.ch-head, .ch-wordmark, .ch-title', 'the header row'],
  ['.ch-card, .ch-card-title, .ch-card-sub, .ch-card-meta', 'a result card'],
  ['.ch-chip, .ch-chip-live, .ch-chip-stale', 'status chips'],
  ['.ch-cta', 'the button that opens the business’s own site'],
  ['.ch-form, .ch-field, .ch-field-label, .ch-form-actions', 'a prepared form'],
  ['.ch-table, .ch-list, .ch-grid', 'the three list layouts'],
  ['.ch-disclaimer', 'the “unofficial” notice on a third-party site'],
];

export function BrandCssPanel(props: { t: TenantDetail; state: DashboardState; actions: DashboardActions }): VNode {
  const { t, state, actions } = props;
  const saved = (t.brand as { customCss?: string | null }).customCss ?? '';
  const draft = state.cssDraft ?? saved;
  const dirty = state.cssDraft !== null && state.cssDraft !== saved;
  const check = checkCustomCss(draft);
  const view = t.views[0];
  const preview = view ? `${t.urls.preview}/${encodeURIComponent(view.id)}?host=claude` : null;

  return html`
    <section class="csspanel" aria-labelledby="css-h">
      <h2 class="sec__h" id="css-h">Widget stylesheet</h2>
      <p class="sec__p">
        The crawl set this site's colours and fonts. What it cannot see is how the business shapes
        its buttons, cards and fields. Write that here and every widget of this site wears it, in
        every chat client. No imports, no <code>url()</code>, nothing that loads or runs.
      </p>

      <div class="csspanel__grid">
        <div>
          <label class="cardlabel" for="css-draft">CSS</label>
          <textarea
            id="css-draft"
            class="csspanel__text"
            spellcheck="false"
            rows="16"
            placeholder=${'.ch-cta {\n  border-radius: 2px;\n  text-transform: uppercase;\n}'}
            value=${draft}
            onInput=${(e: Event) => actions.editCss((e.target as HTMLTextAreaElement).value)}
          ></textarea>
          <p class=${check.ok ? 'csspanel__count' : 'csspanel__count csspanel__count--bad'} role="status">
            ${check.ok ? `${draft.trim().length.toLocaleString('en')} of ${CUSTOM_CSS_MAX.toLocaleString('en')} characters` : check.reason}
          </p>
          <div class="setsec__actions">
            <button
              type="button"
              class="btn"
              disabled=${!dirty || !check.ok || state.savingCss}
              onClick=${() => actions.saveCss()}
            >
              ${state.savingCss ? 'Saving…' : saved && !draft.trim() ? 'Remove stylesheet' : 'Save'}
            </button>
            ${dirty
              ? html`<button type="button" class="btn btn--ghost" onClick=${() => actions.resetCss()}>Revert</button>`
              : null}
          </div>
          ${ErrorNote({ message: state.cssError })}

          <details class="sub">
            <summary class="sub__sum">Selectors you can style</summary>
            <dl class="facts">
              ${CONTRACT.map(
                ([sel, what]) => html`<div class="facts__row">
                  <dt class="facts__k"><code>${sel}</code></dt>
                  <dd class="facts__v">${what}</dd>
                </div>`,
              )}
            </dl>
            <p class="embed__foot">
              The brand's colours are custom properties on <code>.ch-brand</code>${SP}
              (<code>--ch-brand-primary</code>, <code>--ch-bg-surface</code>, <code>--ch-radius-control</code> and so on), so a rule can use
              them instead of repeating a hex value.
            </p>
          </details>
        </div>

        ${preview
          ? html`<div class="appear">
              <div class="appear__head">
                <p class="cardlabel">As saved, in a chat</p>
                <a class="linkish" href=${preview} target="_blank" rel="noopener noreferrer">Open in a full window</a>
              </div>
              <iframe class="appear__frame" src=${preview} title="The first widget of this site, as saved" loading="lazy"></iframe>
            </div>`
          : null}
      </div>
    </section>
  `;
}
